import React, { useState } from 'react';
import { toast } from 'react-toastify';
import moment from 'moment';
import api from '../utils/api';
import { assessmentBasisLabel, assessmentBasisHint } from '../utils/assessmentBasis';
import {
  buildAttendanceExportFilename,
  getDownloadFilename,
  sessionIntakeLabel,
} from '../utils/download';

const FILTERS = ['daily', 'weekly', 'monthly'];

// Pending is the explicit placeholder for active sessions. The absent/empty
// no-clock fallback keeps the UI correct for records created before migration.
const isPendingAttendance = (session, record) =>
  (session.status === 'scheduled' || session.status === 'ongoing') &&
  ((record.status === 'pending' || !record.status) ||
    (record.status === 'absent' && !record.clock_in_time));

function getAttendanceCounts(session) {
  return (session.records || []).reduce((counts, record) => {
    if (isPendingAttendance(session, record)) {
      counts.pending += 1;
    } else if (counts[record.status] !== undefined) {
      counts[record.status] += 1;
    } else {
      counts.absent += 1;
    }
    return counts;
  }, { present: 0, late: 0, mc: 0, pending: 0, absent: 0 });
}

function getDisplayAttendanceStatus(session, record) {
  return isPendingAttendance(session, record) ? 'pending' : (record.status || 'absent');
}

const formatSessionTime = value => value ? String(value).slice(0, 5) : '—';
const STUDENTS_PER_PAGE = 10;
const SESSIONS_PER_PAGE = 10;

const getSessionIntakes = session => {
  if (Array.isArray(session.intakes)) return session.intakes.filter(Boolean);
  return session.intakes ? [session.intakes] : [];
};

const STATUS_META = {
  present: { label: 'Present', icon: 'bi-check-circle-fill' },
  late:    { label: 'Late', icon: 'bi-clock-history' },
  mc:      { label: 'MC', icon: 'bi-file-medical-fill' },
  pending: { label: 'Pending', icon: 'bi-clock' },
  absent:  { label: 'Absent', icon: 'bi-x-circle-fill' },
};

const SESSION_STATUS_META = {
  scheduled: { label: 'Scheduled', icon: 'bi-calendar-event', className: 'badge-scheduled' },
  ongoing:   { label: 'Ongoing', icon: 'bi-play-circle', className: 'badge-ongoing' },
  ended:     { label: 'Ended', icon: 'bi-check-circle', className: 'badge-ended' },
  cancelled: { label: 'Cancelled', icon: 'bi-x-circle', className: 'badge-cancelled' },
};

function getSessionStatusMeta(status) {
  return SESSION_STATUS_META[status] || {
    label: 'Status unavailable',
    icon: 'bi-question-circle',
    className: 'badge-ended',
  };
}

export default function RecordsPage() {
  const [filter, setFilter] = useState('daily');
  const [date, setDate] = useState(moment().format('YYYY-MM-DD'));
  const [sessionType, setSessionType] = useState(''); // '' | 'training' | 'assessment'
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [expandedSessionId, setExpandedSessionId] = useState(null);
  const [studentQuery, setStudentQuery] = useState('');
  const [studentStatusFilter, setStudentStatusFilter] = useState('all');
  const [studentPage, setStudentPage] = useState(1);
  // Filters applied to the sessions already retrieved by Search
  const [sessionQuery, setSessionQuery] = useState('');
  const [intakeFilter, setIntakeFilter] = useState('all');
  const [sessionStatusFilter, setSessionStatusFilter] = useState('all');
  const [sessionPage, setSessionPage] = useState(1);

  function resetSessionFilters() {
    setSessionQuery('');
    setIntakeFilter('all');
    setSessionStatusFilter('all');
    setSessionPage(1);
  }

  function resetStudentList() {
    setStudentQuery('');
    setStudentStatusFilter('all');
    setStudentPage(1);
  }

  function toggleStudentList(sessionId) {
    setExpandedSessionId(currentId => currentId === sessionId ? null : sessionId);
    resetStudentList();
  }

  async function handleSearch() {
    setLoading(true);
    try {
      const res = await api.get('/api/records', { params: { filter, date, session_type: sessionType || undefined } });
      setData(res.data);
      setSearched(true);
      setExpandedSessionId(null);
      resetStudentList();
      resetSessionFilters();
    } catch { toast.error('Failed to load records.'); }
    finally { setLoading(false); }
  }

  async function handleExport() {
    try {
      const res = await api.get('/api/records', { params: { filter, date, export: 'excel', session_type: sessionType || undefined }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const exportSessions = data?.data || [];
      const fallbackFilename = buildAttendanceExportFilename({
        date: exportSessions.length === 1 ? exportSessions[0].session_date : date,
        intakeCode: sessionIntakeLabel(exportSessions),
        period: filter,
      });
      a.download = getDownloadFilename(res.headers, fallbackFilename);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      let message = err.response?.data?.message;
      if (!message && err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          message = JSON.parse(text)?.message;
        } catch (_) {}
      }
      toast.error(message || 'Export failed.');
    }
  }

  const statusBadge = (s, pending = false) => {
    if (pending || s === 'pending') {
      return <span className="status-pending"><i className="bi bi-clock me-1" aria-hidden="true" />Pending Attendance</span>;
    }
    if (s === 'present') return <span className="status-present">🟢 Present</span>;
    if (s === 'late') return <span className="status-late">🟡 Late</span>;
    if (s === 'mc') return <span className="status-mc">🔵 MC</span>;
    return <span className="status-absent">🔴 Absent</span>;
  };

  const allSessions = data?.data || [];
  const intakeOptions = [...new Set(allSessions.flatMap(getSessionIntakes))].sort();
  const normalizedSessionQuery = sessionQuery.trim().toLowerCase();
  const filteredSessions = allSessions.filter(session => {
    if (intakeFilter !== 'all' && !getSessionIntakes(session).includes(intakeFilter)) return false;
    if (sessionStatusFilter !== 'all' && session.status !== sessionStatusFilter) return false;
    if (!normalizedSessionQuery) return true;
    return [session.module_name, session.schedule_name, session.trainer_name, session.venue_name, ...getSessionIntakes(session)]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(normalizedSessionQuery);
  });
  const sessionPageCount = Math.max(1, Math.ceil(filteredSessions.length / SESSIONS_PER_PAGE));
  const currentSessionPage = Math.min(sessionPage, sessionPageCount);
  const visibleSessions = filteredSessions.slice((currentSessionPage - 1) * SESSIONS_PER_PAGE, currentSessionPage * SESSIONS_PER_PAGE);
  const hasSessionFilters = !!normalizedSessionQuery || intakeFilter !== 'all' || sessionStatusFilter !== 'all';

  function changeSessionFilter(setter) {
    return event => {
      setter(event.target.value);
      setSessionPage(1);
      setExpandedSessionId(null);
      resetStudentList();
    };
  }

  function goToSessionPage(page) {
    setSessionPage(page);
    setExpandedSessionId(null);
    resetStudentList();
  }

  const totalRecords = filteredSessions.reduce((a, s) => a + (s.records?.length || 0), 0);
  const isCompactView = filter === 'weekly' || filter === 'monthly';

  return (
    <div>
      <div className="page-header">
        <h1>Attendance Records</h1>
        <p>Search and review attendance history</p>
      </div>

      {/* Filter Bar */}
      <div className="tmis-card mb-3 d-flex flex-wrap gap-3 align-items-center">
        <div className="filter-tab-group">
          {FILTERS.map(f => (
            <button key={f} className={`filter-tab ${filter === f ? 'active' : ''}`} onClick={() => { setFilter(f); setExpandedSessionId(null); resetStudentList(); }}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        <div className="filter-tab-group">
          {[
            { value: '', label: 'All' },
            { value: 'training', label: 'Training' },
            { value: 'assessment', label: 'Assessment' },
          ].map(opt => (
            <button
              key={opt.value || 'all'}
              className={`filter-tab ${sessionType === opt.value ? 'active' : ''}`}
              onClick={() => setSessionType(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <input type="date" className="form-control" style={{ width: 170 }} value={date} onChange={e => setDate(e.target.value)} />
        <button className="btn btn-tmis-primary d-flex align-items-center gap-2" onClick={handleSearch} disabled={loading}>
          {loading ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-search" />}
          Search
        </button>
        {searched && data?.data?.length > 0 && (
          <button className="btn btn-tmis-outline d-flex align-items-center gap-2 ms-auto" onClick={handleExport}>
            <i className="bi bi-file-earmark-excel" />Export Excel
          </button>
        )}
      </div>

      {/* Results */}
      <div className="tmis-card">
        {!searched ? (
          <div className="empty-state">
            <i className="bi bi-table" />
            <h5>Select a date range and search</h5>
            <p style={{ fontSize: '0.875rem' }}>Choose Daily, Weekly, or Monthly and click Search to view records.</p>
          </div>
        ) : loading ? (
          <div className="text-center py-5"><div className="spinner-border" style={{ color: '#17a2b8' }} /></div>
        ) : !data?.data?.length ? (
          <div className="empty-state">
            <i className="bi bi-calendar-x" />
            <h5>No records found</h5>
            <p style={{ fontSize: '0.875rem' }}>No attendance recorded for the selected period.</p>
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <div style={{ fontSize: '0.875rem', color: 'var(--tmis-muted)' }}>
                {hasSessionFilters ? `${filteredSessions.length} of ${allSessions.length}` : filteredSessions.length} session{filteredSessions.length !== 1 ? 's' : ''} · {totalRecords} record{totalRecords !== 1 ? 's' : ''}
                <span className="ms-2">({moment(data.startDate).format('DD/MM/YYYY')} – {moment(data.endDate).format('DD/MM/YYYY')})</span>
              </div>
              {isCompactView && (
                <div className="records-view-hint">
                  <i className="bi bi-info-circle" aria-hidden="true" />
                  Choose <strong>View Students</strong> on a session to inspect its attendance list.
                </div>
              )}
            </div>

            <div className="records-result-filters">
              <label className="visually-hidden" htmlFor="records-session-search">Search retrieved sessions</label>
              <div className="input-group input-group-sm records-session-student-search">
                <span className="input-group-text" aria-hidden="true"><i className="bi bi-search" /></span>
                <input
                  id="records-session-search"
                  type="search"
                  className="form-control"
                  placeholder="Search module, trainer, venue or intake"
                  value={sessionQuery}
                  onChange={changeSessionFilter(setSessionQuery)}
                />
              </div>
              <label className="visually-hidden" htmlFor="records-session-intake">Filter sessions by intake</label>
              <select
                id="records-session-intake"
                className="form-select form-select-sm records-session-student-status"
                value={intakeFilter}
                onChange={changeSessionFilter(setIntakeFilter)}
              >
                <option value="all">All intakes</option>
                {intakeOptions.map(intake => <option key={intake} value={intake}>{intake}</option>)}
              </select>
              <label className="visually-hidden" htmlFor="records-session-status">Filter sessions by status</label>
              <select
                id="records-session-status"
                className="form-select form-select-sm records-session-student-status"
                value={sessionStatusFilter}
                onChange={changeSessionFilter(setSessionStatusFilter)}
              >
                <option value="all">All statuses</option>
                {Object.entries(SESSION_STATUS_META).map(([value, meta]) => <option key={value} value={value}>{meta.label}</option>)}
              </select>
              {hasSessionFilters && (
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={resetSessionFilters}>
                  <i className="bi bi-x-circle me-1" aria-hidden="true" />Clear
                </button>
              )}
            </div>

            {filteredSessions.length === 0 ? (
              <div className="empty-state">
                <i className="bi bi-funnel" />
                <h5>No sessions match your filters</h5>
              </div>
            ) : (
            <div className="records-session-list">
              {visibleSessions.map(session => {
                const counts = getAttendanceCounts(session);
                const sessionDate = moment(session.session_date);
                const sessionStatus = getSessionStatusMeta(session.status);
                const trainerName = session.trainer_name || 'Unassigned';
                const isStudentsExpanded = !isCompactView || expandedSessionId === session.id;
                const sessionStudents = session.records || [];
                const normalizedQuery = studentQuery.trim().toLowerCase();
                const filteredStudents = sessionStudents.filter(record => {
                  if (!isCompactView || !isStudentsExpanded) return true;
                  const searchable = [record.student_id, record.student_name, record.student_email, record.intake]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase();
                  const status = getDisplayAttendanceStatus(session, record);
                  return (!normalizedQuery || searchable.includes(normalizedQuery)) &&
                    (studentStatusFilter === 'all' || status === studentStatusFilter);
                });
                const studentPageCount = Math.max(1, Math.ceil(filteredStudents.length / STUDENTS_PER_PAGE));
                const currentStudentPage = Math.min(studentPage, studentPageCount);
                const visibleStudents = isCompactView
                  ? filteredStudents.slice((currentStudentPage - 1) * STUDENTS_PER_PAGE, currentStudentPage * STUDENTS_PER_PAGE)
                  : filteredStudents;
                const statusKeys = ['present', 'late', 'mc']
                  .concat(counts.pending > 0 ? ['pending'] : [], ['absent']);

                return (
                  <section key={session.id} className="records-session" aria-labelledby={`records-session-${session.id}`}>
                    <div className="records-session-header">
                      <div className="records-session-date" aria-label={sessionDate.format('DD MMM YYYY')}>
                        <i className="bi bi-calendar3" aria-hidden="true" />
                        <strong>{sessionDate.format('DD')}</strong>
                        <span>{sessionDate.format('MMM')}</span>
                      </div>

                      <div className="records-session-heading">
                        <div className="records-session-badges" aria-label="Session type and status">
                          <span className={`badge records-session-type ${session.session_type === 'assessment' ? 'is-assessment' : 'is-training'}`}>
                            <i className={`bi ${session.session_type === 'assessment' ? 'bi-clipboard2-check-fill' : 'bi-mortarboard-fill'} me-1`} aria-hidden="true" />
                            {session.session_type === 'assessment' ? 'Assessment' : 'Training'}
                          </span>
                          {session.session_type === 'assessment' && !!session.assessment_basis && (
                            <span
                              className="badge records-session-type records-session-basis"
                              title={assessmentBasisHint(session.assessment_basis)}
                            >
                              <i className="bi bi-bar-chart-steps me-1" aria-hidden="true" />
                              {assessmentBasisLabel(session.assessment_basis)}
                            </span>
                          )}
                          <span className={`session-badge records-session-status ${sessionStatus.className}`}>
                            <i className={`bi ${sessionStatus.icon} me-1`} aria-hidden="true" />
                            {sessionStatus.label}
                          </span>
                        </div>
                        <div className="records-session-title-row">
                          <div className="records-session-title-group">
                            <h3 id={`records-session-${session.id}`}>
                              {session.module_name || session.schedule_name}
                              {session.module_name && session.module_name !== session.schedule_name && (
                                <span className="records-session-module-code">{session.schedule_name}</span>
                              )}
                            </h3>
                          </div>
                        </div>
                        <div className="records-session-meta">
                          <span className="records-session-meta-item"><i className="bi bi-clock" aria-hidden="true" />{formatSessionTime(session.start_time)}{session.end_time ? ` – ${formatSessionTime(session.end_time)}` : ''}</span>
                          {session.venue_name && <span className="records-session-meta-item"><i className="bi bi-geo-alt" aria-hidden="true" />{session.venue_name}</span>}
                          {getSessionIntakes(session).length > 0 && (
                            <span className="records-session-meta-item" title="Intake(s)">
                              <i className="bi bi-people" aria-hidden="true" />{getSessionIntakes(session).join(', ')}
                            </span>
                          )}
                          <span className="records-session-trainer" title={`Trainer: ${trainerName}`}>
                            <i className="bi bi-person-badge" aria-hidden="true" />
                            <span>Trainer:</span>
                            <strong>{trainerName}</strong>
                          </span>
                        </div>
                      </div>

                      <div className="records-session-actions">
                        <div className="records-session-counts" aria-label="Attendance summary">
                          {statusKeys.map(key => (
                            <span key={key} className={`stat-pill ${key} records-session-count`} title={`${STATUS_META[key].label}: ${counts[key]}`}>
                              <i className={`bi ${STATUS_META[key].icon}`} aria-hidden="true" />
                              <span className="records-session-count-label">{STATUS_META[key].label}</span>
                              <strong>{counts[key]}</strong>
                            </span>
                          ))}
                        </div>
                        {isCompactView && (
                          <button
                            type="button"
                            className={`btn btn-xs btn-outline-secondary records-session-student-toggle ${isStudentsExpanded ? 'is-expanded' : ''}`}
                            onClick={() => toggleStudentList(session.id)}
                            aria-expanded={isStudentsExpanded}
                            aria-controls={`records-students-${session.id}`}
                          >
                            <i className={`bi ${isStudentsExpanded ? 'bi-chevron-up' : 'bi-people'}`} aria-hidden="true" />
                            {isStudentsExpanded ? 'Hide Students' : `View Students (${session.records?.length || 0})`}
                          </button>
                        )}
                      </div>
                    </div>

                    {isStudentsExpanded && (
                      <div id={`records-students-${session.id}`} className="records-session-students">
                        {isCompactView && (
                          <div className="records-session-student-toolbar">
                            <label className="visually-hidden" htmlFor={`records-student-search-${session.id}`}>
                              Search students in this session
                            </label>
                            <div className="input-group input-group-sm records-session-student-search">
                              <span className="input-group-text" aria-hidden="true"><i className="bi bi-search" /></span>
                              <input
                                id={`records-student-search-${session.id}`}
                                type="search"
                                className="form-control"
                                placeholder="Search students"
                                value={studentQuery}
                                onChange={event => { setStudentQuery(event.target.value); setStudentPage(1); }}
                              />
                            </div>
                            <label className="visually-hidden" htmlFor={`records-student-status-${session.id}`}>
                              Filter students by attendance status
                            </label>
                            <select
                              id={`records-student-status-${session.id}`}
                              className="form-select form-select-sm records-session-student-status"
                              value={studentStatusFilter}
                              onChange={event => { setStudentStatusFilter(event.target.value); setStudentPage(1); }}
                            >
                              <option value="all">All statuses</option>
                              <option value="pending">Pending Attendance</option>
                              <option value="present">Present</option>
                              <option value="late">Late</option>
                              <option value="mc">MC</option>
                              <option value="absent">Absent</option>
                            </select>
                          </div>
                        )}
                        <div className="table-responsive records-session-table">
                          <table className="table tmis-table table-sm mb-0">
                            <thead>
                              <tr>
                                <th className="text-center">#</th><th className="text-center">Student ID</th><th>Name</th><th>Email</th><th className="text-center">Intake</th>
                                <th className="text-center">Clock In</th><th className="text-center">Clock Out</th><th className="text-center">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {visibleStudents.length === 0 ? (
                                <tr><td colSpan="8" className="records-session-no-students">No students match your search or status filter.</td></tr>
                              ) : visibleStudents.map((r, i) => (
                                <tr key={r.id}>
                                  <td className="text-muted text-center">{isCompactView ? (currentStudentPage - 1) * STUDENTS_PER_PAGE + i + 1 : i + 1}</td>
                                  <td className="text-center">{r.student_id || '-'}</td>
                                  <td>{r.student_name}</td>
                                  <td>{r.student_email}</td>
                                  <td className="text-center">{r.intake || '-'}</td>
                                  <td className="text-center">{r.clock_in_time ? moment(r.clock_in_time).format('HH:mm:ss') : '-'}</td>
                                  <td className="text-center">{r.clock_out_time ? moment(r.clock_out_time).format('HH:mm:ss') : '-'}</td>
                                  <td className="text-center">{statusBadge(r.status, isPendingAttendance(session, r))}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {isCompactView && (
                          <div className="records-session-student-footer">
                            <span>
                              {filteredStudents.length === 0
                                ? '0 students shown'
                                : `Showing ${(currentStudentPage - 1) * STUDENTS_PER_PAGE + 1}–${Math.min(currentStudentPage * STUDENTS_PER_PAGE, filteredStudents.length)} of ${filteredStudents.length} students`}
                            </span>
                            {studentPageCount > 1 && (
                              <div className="records-session-student-pagination" aria-label="Student list pagination">
                                <button
                                  type="button"
                                  className="btn btn-xs btn-outline-secondary"
                                  disabled={currentStudentPage === 1}
                                  onClick={() => setStudentPage(pageNumber => Math.max(1, pageNumber - 1))}
                                >
                                  <i className="bi bi-chevron-left" aria-hidden="true" /> Previous
                                </button>
                                <span>Page {currentStudentPage} of {studentPageCount}</span>
                                <button
                                  type="button"
                                  className="btn btn-xs btn-outline-secondary"
                                  disabled={currentStudentPage === studentPageCount}
                                  onClick={() => setStudentPage(pageNumber => Math.min(studentPageCount, pageNumber + 1))}
                                >
                                  Next <i className="bi bi-chevron-right" aria-hidden="true" />
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
            )}

            {sessionPageCount > 1 && (
              <div className="records-session-pager">
                <span>
                  Showing {(currentSessionPage - 1) * SESSIONS_PER_PAGE + 1}–{Math.min(currentSessionPage * SESSIONS_PER_PAGE, filteredSessions.length)} of {filteredSessions.length} sessions
                </span>
                <div className="records-session-student-pagination" aria-label="Session list pagination">
                  <button
                    type="button"
                    className="btn btn-xs btn-outline-secondary"
                    disabled={currentSessionPage === 1}
                    onClick={() => goToSessionPage(currentSessionPage - 1)}
                  >
                    <i className="bi bi-chevron-left" aria-hidden="true" /> Previous
                  </button>
                  <span>Page {currentSessionPage} of {sessionPageCount}</span>
                  <button
                    type="button"
                    className="btn btn-xs btn-outline-secondary"
                    disabled={currentSessionPage === sessionPageCount}
                    onClick={() => goToSessionPage(currentSessionPage + 1)}
                  >
                    Next <i className="bi bi-chevron-right" aria-hidden="true" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
